import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  FileSpreadsheet,
  History,
  Package,
  Pencil,
  Search,
  Store,
  Upload,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDT } from "@/lib/sound";
import { useImportJob } from "@/hooks/useImportJob";
import { ImportProgress, ResumoImportacao } from "@/components/ImportProgress";
import { MapaEnderecos } from "@/components/MapaEnderecos";
import { ConsultaAbastecimento } from "@/components/ConsultaAbastecimento";
import { CadastroProdutosCard } from "@/components/CadastroProdutosCard";
import { ProdutoCadastroDialog } from "@/components/ProdutoCadastroDialog";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";
const TABS = [
  ["mapa", "Mapa"],
  ["consulta", "Consultar lista"],
  ["produtos", "Produtos"],
  ["alocados", "Alocados"],
  ["historico", "Histórico"],
];

function ImportCard({ id, titulo, sub, icon: Icon, url, ultima, onDone }) {
  const ref = useRef(null);
  const [resumo, setResumo] = useState(null);
  const { job, start, running } = useImportJob((r) => { setResumo(r); onDone?.(r); });
  return (
    <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid={`import-card-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#EAF7EF] flex items-center justify-center"><Icon className="w-5 h-5 text-[#0F6B3A]" /></div>
          <div>
            <div className="break-words font-bold text-[#0F2A1F]">{titulo}</div>
            <div className="break-words text-xs text-[#5A6B62]">{sub}</div>
          </div>
        </div>
        <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid={`import-input-${id}`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) start(url, f); e.target.value = ""; }} />
        <button data-testid={`import-btn-${id}`} disabled={running} onClick={() => ref.current?.click()}
          className="shrink-0 bg-[#0F6B3A] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0B5530] disabled:opacity-60 rounded-lg flex items-center gap-2"
        >
          <Upload className="w-4 h-4" /> Importar
        </button>
      </div>
      <ImportProgress job={job} testid={`import-progress-${id}`} />
      {resumo ? <ResumoImportacao r={resumo} testid={`import-resumo-${id}`} /> : ultima ? (
        <div className="mt-3 text-[11px] text-[#8A9A90] font-mono" data-testid={`import-ultima-${id}`}>
          Última: {ultima.novo?.arquivo} · {ultima.usuario} · {fmtDT(ultima.at)} · {ultima.novo?.incluidos} incluídos
        </div>
      ) : null}
    </div>
  );
}

export default function Enderecos() {
  const [tab, setTab] = useState("consulta");
  const [mapaTipo, setMapaTipo] = useState("separacao");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [produtoEmEdicao, setProdutoEmEdicao] = useState(null);
  const [cadastroAberto, setCadastroAberto] = useState(false);

  const fetchStats = useCallback(async () => {
    try { setStats((await api.get("/enderecos/stats")).data); } catch { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    if (tab === "mapa") { setLoading(false); return; }
    if (tab === "consulta") { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      if (tab === "produtos") setItems((await api.get("/produtos", { params: { q: q || undefined } })).data);
      else if (tab === "alocados") setItems((await api.get("/produtos/alocados", { params: { q: q || undefined } })).data);
      else setItems((await api.get("/enderecos/historico")).data);
    } catch { toast.error("Erro ao carregar dados"); } finally { setLoading(false); }
  }, [tab, q]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); }, [fetchList]);

  const kpis = stats ? [
    ["Posições Separação", stats.separacao.posicoes, `${stats.separacao.codigos} códigos · ${stats.separacao.vazias} vazias`],
    ["Posições Pulmão", stats.pulmao.posicoes, `${stats.pulmao.codigos} códigos · ${stats.pulmao.vazias} vazias`],
    ["Produtos no catálogo", stats.produtos, "código, nome e saldos"],
    ["Com reserva online", stats.alocados, "alocado > 0"],
  ] : [];

  return (
    <div className="space-y-5" data-testid="enderecos-page">
      <div>
        <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Estoque</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">Endereçamento</h1>
        <p className="text-sm text-[#5A6B62] mt-1">Mapa de posições por rack, bipagem de verificação e catálogo de Produtos.</p>
      </div>

      <div className={`grid-cols-1 gap-4 lg:grid-cols-2 ${tab === "mapa" ? "hidden md:grid" : "grid"}`}>
        <ImportCard id="separacao" titulo="Estoque de Separação" sub="Pick list · .xlsx/.csv · substituição total" icon={FileSpreadsheet} url="/estoque/import/separacao" ultima={stats?.ultimas_importacoes?.separacao} onDone={fetchStats} />
        <ImportCard id="pulmao" titulo="Estoque Pulmão" sub="Reserva · .xlsx/.csv · substituição total" icon={Warehouse} url="/estoque/import/pulmao" ultima={stats?.ultimas_importacoes?.pulmao} onDone={fetchStats} />
        <ImportCard id="produtos" titulo="Produtos (PosicaoEstoque)" sub="Codigo Material · Nome · Total Físico/Alocado/Disponível · upsert" icon={Package} url="/estoque/import/produtos" ultima={stats?.ultimas_importacoes?.produtos} onDone={fetchStats} />
        <CadastroProdutosCard
          ultima={stats?.ultimas_importacoes?.cadastro}
          onDone={() => { fetchStats(); fetchList(); }}
          onCadastrar={() => { setProdutoEmEdicao(null); setCadastroAberto(true); }}
        />
      </div>

      <div className={`grid-cols-2 xl:grid-cols-4 gap-4 ${tab === "mapa" ? "hidden md:grid" : "grid"}`}>
        {kpis.map(([t, v, s], i) => (
          <div key={t} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid={`end-kpi-${i}`}>
            <p className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">{t}</p>
            <p className="text-3xl font-extrabold text-[#0F2A1F] mt-1 font-mono">{v}</p>
            <p className="text-[11px] text-[#8A9A90]">{s}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap border border-[#D9E3DC] rounded-lg bg-white" data-testid="enderecos-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => { setTab(k); setQ(""); setItems([]); if (k !== "mapa") setLoading(true); }}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-[#2DC26B] text-white" : "text-[#0F2A1F] hover:bg-[#F1F5F2]"}`}>{l}</button>
        ))}
      </div>

      {tab === "mapa" && (
        <>
          <div className="inline-flex border border-[#D9E3DC] rounded-lg overflow-hidden bg-white" data-testid="mapa-tipo-toggle">
            {[["separacao", "Estoque de Separação"], ["pulmao", "Estoque Pulmão"]].map(([k, l]) => (
              <button key={k} data-testid={`mapa-tipo-${k}`} onClick={() => setMapaTipo(k)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${mapaTipo === k ? "bg-[#0F6B3A] text-white" : "text-[#0F2A1F] hover:bg-[#F1F5F2]"}`}>{l}</button>
            ))}
          </div>
          <MapaEnderecos tipo={mapaTipo} onChanged={fetchStats} />
        </>
      )}

      {tab === "consulta" && <ConsultaAbastecimento />}

      {(tab === "produtos" || tab === "alocados") && (
        <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="enderecos-busca-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} placeholder="Buscar por código, EAN ou descrição..." />
          </div>
        </div>
      )}

      {["produtos", "alocados", "historico"].includes(tab) && <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? <div className="py-10 text-center text-[#8A9A90]">Carregando...</div>
            : items.length === 0 ? <div className="py-10 text-center text-[#8A9A90]" data-testid="enderecos-empty">Nenhum registro encontrado</div>
            : tab === "produtos" ? (
              <table className="w-full text-sm" data-testid="produtos-table">
                <thead><tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Código</th><th className="text-left py-3 px-4">Nome</th><th className="text-left py-3 px-4">EAN</th><th className="text-right py-3 px-4">Un./caixa</th><th className="text-right py-3 px-4">Física</th><th className="text-right py-3 px-4">Alocado</th><th className="text-right py-3 px-4">Disponível</th><th className="text-right py-3 px-4">Saldo pulmão</th><th className="text-right py-3 px-4">Ação</th></tr></thead>
                <tbody>{items.map((p, idx) => (
                  <tr key={`${p.codigo}-${idx}`} className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]" data-testid={`produto-row-${p.codigo}`}>
                    <td className="py-2.5 px-4 font-mono font-semibold text-[#0F6B3A]">{p.codigo}</td>
                    <td className="py-2.5 px-4 text-[#0F2A1F]">{p.nome}</td>
                    <td className="py-2.5 px-4 font-mono text-xs" data-testid={`produto-ean-${p.codigo}`}>{p.ean || "—"}</td>
                    <td className="py-2.5 px-4 text-right font-mono" data-testid={`produto-unidades-caixa-${p.codigo}`}>{p.unidades_por_caixa ?? "—"}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{p.fisica}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-[#0F6B3A]">{p.alocado}</td>
                    <td className={`py-2.5 px-4 text-right font-mono font-bold ${p.disponivel <= 0 ? "text-[#B91C1C]" : p.disponivel <= 2 ? "text-[#B8860B]" : "text-[#047857]"}`}>{p.disponivel}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{p.saldo_pulmao ?? "—"}</td>
                    <td className="py-2.5 px-4 text-right">
                      <button
                        title={`Editar ${p.codigo}`}
                        data-testid={`produto-editar-${p.codigo}`}
                        onClick={() => { setProdutoEmEdicao(p); setCadastroAberto(true); }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#0F6B3A] transition-colors hover:bg-[#EAF7EF]"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>))}</tbody>
              </table>
            ) : tab === "alocados" ? (
              <table className="w-full text-sm" data-testid="alocados-table">
                <thead><tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Código</th><th className="text-left py-3 px-4">Nome</th><th className="text-right py-3 px-4">Alocado</th><th className="text-right py-3 px-4">Disponível</th><th className="text-left py-3 px-4">Alertas</th><th className="text-left py-3 px-4">Endereços</th></tr></thead>
                <tbody>{items.map((p, idx) => (
                  <tr key={`${p.codigo}-${idx}`} className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]" data-testid={`alocado-row-${p.codigo}`}>
                    <td className="py-2.5 px-4 font-mono font-semibold text-[#0F6B3A]">{p.codigo}</td>
                    <td className="py-2.5 px-4 text-[#0F2A1F]">{p.nome}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-bold text-[#0F6B3A]">{p.alocado}</td>
                    <td className={`py-2.5 px-4 text-right font-mono font-bold ${p.disponivel <= 0 ? "text-[#B91C1C]" : "text-[#0F2A1F]"}`}>{p.disponivel}</td>
                    <td className="py-2.5 px-4 space-x-1">
                      {p.risco && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-bold uppercase"><AlertTriangle className="w-3 h-3" />Risco: alocado &gt; disponível</span>}
                      {p.pode_estar_na_loja && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FFF4D6] text-[#9A6B0A] text-[10px] font-bold uppercase"><Store className="w-3 h-3" />Pode estar na loja · verificar</span>}
                    </td>
                    <td className="py-2.5 px-4 font-mono text-xs text-[#5A6B62]">{(p.localizacoes || []).map((l) => `${l.endereco}${l.tipo === "pulmao" ? " (P)" : ""}`).join(" · ") || "—"}</td>
                  </tr>))}</tbody>
              </table>
            ) : (
              <table className="w-full text-sm" data-testid="historico-table">
                <thead><tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Quando</th><th className="text-left py-3 px-4">Base</th><th className="text-left py-3 px-4">Ação</th><th className="text-left py-3 px-4">Endereço / Código</th><th className="text-left py-3 px-4">Alteração</th><th className="text-left py-3 px-4">Usuário</th></tr></thead>
                <tbody>{items.map((h, idx) => (
                  <tr key={h.id || idx} className="border-b border-[#F1F5F2]" data-testid={`historico-row-${h.id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs text-[#5A6B62] whitespace-nowrap">{fmtDT(h.at)}</td>
                    <td className="py-2.5 px-4 text-xs uppercase font-semibold text-[#0F2A1F]">{h.tipo}</td>
                    <td className="py-2.5 px-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${h.acao === "REMOVIDO" ? "bg-[#FEE2E2] text-[#B91C1C]" : h.acao === "CRIADO" ? "bg-[#D1FAE5] text-[#047857]" : h.acao === "IMPORTACAO" ? "bg-[#EAF7EF] text-[#0F6B3A]" : "bg-[#FFF4D6] text-[#9A6B0A]"}`}>{h.acao}</span></td>
                    <td className="py-2.5 px-4 font-mono text-xs text-[#0F2A1F]">{h.endereco || "—"} {h.codigo ? `· ${h.codigo}` : ""}</td>
                    <td className="py-2.5 px-4 text-xs text-[#5A6B62]">
                      {h.acao === "IMPORTACAO" ? <span className="flex items-center gap-1"><History className="w-3 h-3" />{h.novo?.arquivo}: +{h.novo?.incluidos} / ~{h.novo?.alterados} / −{h.novo?.removidos} · {h.novo?.vazios} vazios · {h.novo?.duplicados} dup.</span>
                        : h.acao === "ALTERADO" ? <span><b>{h.campo}</b>: <span className="line-through text-[#B91C1C]">{String(h.anterior ?? "—")}</span> → <span className="text-[#047857] font-semibold">{String(h.novo ?? "—")}</span></span>
                        : h.acao === "REMOVIDO" ? <span>{h.anterior?.codigo || "vazio"} removido de {h.anterior?.endereco}</span>
                        : <span>{h.novo?.codigo || "vazio"} em {h.novo?.endereco}</span>}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-[#5A6B62]">{h.usuario}</td>
                  </tr>))}</tbody>
              </table>
            )}
        </div>
        {items.length >= 300 && tab !== "historico" && <div className="px-4 py-2 text-[11px] text-[#8A9A90] border-t border-[#F1F5F2]">Mostrando os primeiros {items.length} registros — refine a busca.</div>}
      </div>}
      <ProdutoCadastroDialog
        open={cadastroAberto}
        produto={produtoEmEdicao}
        onOpenChange={setCadastroAberto}
        onSaved={() => { fetchStats(); fetchList(); }}
      />
    </div>
  );
}
